/**
 * PPM Auth Gate
 * ------------
 * Intercepts actions that require a logged-in user (e.g. processToPayment).
 * When the user is not authenticated it:
 *   1. Snapshots the current text-field form data from all PPM inputs.
 *   2. POSTs that snapshot to /api/store-invitation-data/ (session-side save).
 *   3. Shows the in-PPM login overlay (#ppm-auth-overlay).
 *   4. After a successful AJAX login it repopulates all text inputs from the
 *      snapshot and invokes the originally-requested callback.
 *
 * File/media inputs (image, video, audio) are intentionally skipped — the
 * user has to re-select those after login, which is acceptable per spec.
 */

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Call this instead of running a login-required action directly.
 * If the user is already logged in the callback fires immediately.
 * Otherwise the PPM login overlay is shown; the callback fires after login.
 *
 * @param {Function} callback  Zero-argument function to run when authenticated.
 */
/**
 * Like openPPM but gates non-details tabs behind authentication.
 * Safe to use from any onclick — passes through immediately when authenticated
 * or when the target is 'details' (which is always accessible).
 */
window.openPPMWithAuth = function openPPMWithAuth(tab) {
    if (tab === 'details' || window.userIsAuthenticated) {
        if (typeof window.openPPM === 'function') window.openPPM(tab);
        return;
    }
    var overlayMsg = document.getElementById('ppm-auth-subtitle');
    if (overlayMsg) overlayMsg.textContent = 'Sign in to edit your invitation.';
    window.requireAuth(function () { if (typeof window.openPPM === 'function') window.openPPM(tab); });
};

window.requireAuth = function requireAuth(callback) {
    if (window.userIsAuthenticated) {
        callback();
        return;
    }

    // Snapshot all current text-field values
    const snapshot = _snapFormData();

    // Persist snapshot to the Django session (so the existing login flow can
    // also recover it if the user navigates away and does a full login).
    const templateId = document.body.getAttribute('data-template-id') || '';
    const themeId    = document.body.getAttribute('data-theme-id')    || '';
    const payload    = { ...snapshot, template_id: templateId, theme_id: themeId };

    fetch('/api/store-invitation-data/', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': _getCsrf(),
        },
        body: JSON.stringify(payload),
    }).catch(() => { /* non-critical — overlay still shows */ });

    // Stash callback + snapshot for use after login
    window._ppmAuthCallback    = callback;
    window._ppmFormSnapshot    = snapshot;

    // Restore the payment-context message before showing
    var overlayMsg = document.getElementById('ppm-auth-subtitle');
    if (overlayMsg) overlayMsg.textContent = 'Your customizations are saved \u2014 sign in to proceed.';

    _showOverlay();
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _snapFormData() {
    // Re-use the existing getInvitationData() helper if it is available on the
    // window, otherwise fall back to a simple DOM scan.
    if (typeof window.getInvitationData === 'function') {
        try { return window.getInvitationData(); } catch (_) { /* fallback */ }
    }

    const data = {};
    document.querySelectorAll('[data-theme-key]').forEach(function (el) {
        // Skip file / media inputs
        if (el.type === 'file') return;
        const mt = (el.getAttribute('data-media-type') || '').toLowerCase();
        if (mt === 'image' || mt === 'video' || mt === 'audio') return;

        const key = el.getAttribute('data-theme-key');
        if (!key) return;

        let value;
        if (el.type === 'checkbox')      value = el.checked;
        else if (el.type === 'radio')    { if (el.checked) value = el.value; else return; }
        else                             value = el.value;

        if (value === undefined || value === '') return;
        data[key] = value;
    });
    return data;
}

function _repopulateForm(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return;

    document.querySelectorAll('[data-theme-key]').forEach(function (el) {
        if (el.type === 'file') return;
        const mt = (el.getAttribute('data-media-type') || '').toLowerCase();
        if (mt === 'image' || mt === 'video' || mt === 'audio') return;

        const key = el.getAttribute('data-theme-key');
        if (!key) return;

        // Resolve potentially nested keys ("rsvp.guests_count")
        const parts = key.split('.');
        let val = snapshot;
        for (const p of parts) {
            if (val && typeof val === 'object' && p in val) val = val[p];
            else { val = undefined; break; }
        }
        if (val === undefined || val === null) return;

        if (el.type === 'checkbox') {
            el.checked = !!val;
            el.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (el.type === 'radio') {
            el.checked = (el.value === String(val));
        } else {
            el.value = val;
            el.dispatchEvent(new Event('input', { bubbles: true }));
        }
    });

    // Also push back into formDataStorage in-memory store if available
    if (window.formDataStorage && typeof window.formDataStorage.saveFormData === 'function') {
        window.formDataStorage.saveFormData(snapshot);
    }
}

function _showOverlay() {
    const overlay = document.getElementById('ppm-auth-overlay');
    if (!overlay) return;
    overlay.style.display = 'block';
    // Ensure the PPM popup is visible (openPPM may have been called already)
    const popup = document.getElementById('ppm-popup');
    if (popup && popup.getAttribute('data-popup-state') !== 'show') {
        if (typeof window.openPPM === 'function') window.openPPM('details');
    }
    // Focus first visible input
    requestAnimationFrame(function () {
        const first = overlay.querySelector('input:not([type=hidden])');
        if (first) first.focus();
    });
}

function _hideOverlay() {
    const overlay = document.getElementById('ppm-auth-overlay');
    if (!overlay) return;
    overlay.style.display = 'none';
    const err = document.getElementById('ppm-auth-error');
    if (err) { err.textContent = ''; err.style.display = 'none'; }
    // Clear password field for security
    const pwd = document.getElementById('ppm-auth-password');
    if (pwd) pwd.value = '';
}

window.hidePPMLoginOverlay = _hideOverlay;
window.showPPMLoginOverlay = _showOverlay;

// Resolves a reCAPTCHA v3 token for the given action, or '' when reCAPTCHA
// isn't configured (window.RECAPTCHA_ENABLED is set inline by the page template).
function _getRecaptchaToken(action) {
    if (!window.RECAPTCHA_ENABLED || typeof grecaptcha === 'undefined') return Promise.resolve('');
    return new Promise(function (resolve) {
        grecaptcha.ready(function () {
            grecaptcha.execute(window.RECAPTCHA_SITE_KEY, { action: action }).then(resolve).catch(function () { resolve(''); });
        });
    });
}

function _getCsrf() {
    if (typeof getCookie === 'function')        return getCookie('csrftoken');
    if (typeof window.getCookie === 'function') return window.getCookie('csrftoken');
    // Read from cookie manually
    const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    return match ? match[1] : '';
}

// ---------------------------------------------------------------------------
// DOM-ready: bind overlay form + controls
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', function () {
    const overlay = document.getElementById('ppm-auth-overlay');
    if (!overlay) return; // Not rendered (user is already authenticated)

    // ── Close button ────────────────────────────────────────────────────────
    const closeBtn = document.getElementById('ppm-auth-overlay-close');
    if (closeBtn) {
        closeBtn.addEventListener('click', _hideOverlay);
    }

    // ── Password visibility toggle ───────────────────────────────────────────
    const togglePwd = document.getElementById('ppm-auth-toggle-pwd');
    if (togglePwd) {
        togglePwd.addEventListener('click', function () {
            const pwdInput = document.getElementById('ppm-auth-password');
            if (!pwdInput) return;
            if (pwdInput.type === 'password') {
                pwdInput.type = 'text';
                this.querySelector('i').classList.replace('bi-eye-slash', 'bi-eye');
            } else {
                pwdInput.type = 'password';
                this.querySelector('i').classList.replace('bi-eye', 'bi-eye-slash');
            }
        });
    }

    // ── Login form submission ────────────────────────────────────────────────
    const form = document.getElementById('ppm-auth-form');
    if (!form) return;

    form.addEventListener('submit', async function (e) {
        e.preventDefault();

        const submitBtn  = form.querySelector('button[type="submit"]');
        const btnText    = submitBtn ? submitBtn.querySelector('.btn-text')   : null;
        const btnLoader  = submitBtn ? submitBtn.querySelector('.btn-loader') : null;
        const errorBox   = document.getElementById('ppm-auth-error');

        // Show loader
        if (btnText)   btnText.classList.add('d-none');
        if (btnLoader) btnLoader.classList.remove('d-none');
        if (submitBtn) submitBtn.disabled = true;
        if (errorBox)  { errorBox.textContent = ''; errorBox.style.display = 'none'; }

        try {
            const identifier = (document.getElementById('ppm-auth-identifier')?.value || '').trim();
            const password   =  document.getElementById('ppm-auth-password')?.value   || '';
            const csrfToken  = _getCsrf();

            if (!identifier || !password) {
                throw new Error('please_fill');
            }

            const recaptchaToken = await _getRecaptchaToken('ajax_login');

            const fd = new FormData();
            fd.append('username_email_phone', identifier);
            fd.append('password', password);
            fd.append('csrfmiddlewaretoken', csrfToken);
            fd.append('recaptcha_token', recaptchaToken);

            const resp = await fetch('/auth/ajax-login/', {
                method: 'POST',
                credentials: 'same-origin',
                body: fd,
            });

            // Try to parse JSON regardless of HTTP status
            let data = {};
            try { data = await resp.json(); } catch (_) { /* empty */ }

            if (data.success) {
                // Update CSRF cookie value used by subsequent requests
                // (Django already set the Set-Cookie header; we just update the
                // in-page tracking variable used by getCookie() shims.)
                if (data.csrf_token) {
                    const meta = document.querySelector('meta[name="csrf-token"]');
                    if (meta) meta.setAttribute('content', data.csrf_token);
                }

                window.userIsAuthenticated = true;

                // Restore form data from snapshot
                _repopulateForm(window._ppmFormSnapshot || {});

                // Hide overlay
                _hideOverlay();

                // Fire the pending callback
                const cb = window._ppmAuthCallback;
                window._ppmAuthCallback = null;
                if (typeof cb === 'function') cb();

            } else {
                const msg = data.message || 'Login failed. Please check your credentials and try again.';
                if (errorBox) { errorBox.textContent = msg; errorBox.style.display = 'block'; }
            }

        } catch (err) {
            let msg = 'A network error occurred. Please try again.';
            if (err.message === 'please_fill') msg = 'Please enter your email / username and password.';
            if (errorBox) { errorBox.textContent = msg; errorBox.style.display = 'block'; }
        } finally {
            if (btnText)   btnText.classList.remove('d-none');
            if (btnLoader) btnLoader.classList.add('d-none');
            if (submitBtn) submitBtn.disabled = false;
        }
    });
});
